import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Check, X, Zap, Star, Shield, Users, Clock, Award,
  BookOpen, MessageCircle, Video, FileText
} from 'lucide-react';
import axios from 'axios';
import { trackButtonClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const plans = [
  {
    id: 'standard',
    name: 'Standard',
    price: 499,
    originalPrice: 999,
    description: 'Everything you need to master AI & ML',
    popular: false,
    features: [
      { text: 'Full course access (120+ hours)', included: true },
      { text: '25+ hands-on projects', included: true },
      { text: 'Lifetime access & updates', included: true },
      { text: 'Certificate of completion', included: true },
      { text: 'Community forum access', included: true },
      { text: 'Downloadable resources', included: true },
      { text: '1-on-1 mentoring sessions', included: false },
      { text: 'Career coaching', included: false },
      { text: 'Resume review', included: false },
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 799,
    originalPrice: 1499,
    description: 'Best for career transformation',
    popular: true,
    features: [
      { text: 'Full course access (120+ hours)', included: true },
      { text: '25+ hands-on projects', included: true },
      { text: 'Lifetime access & updates', included: true },
      { text: 'Certificate of completion', included: true },
      { text: 'Community forum access', included: true },
      { text: 'Downloadable resources', included: true },
      { text: '4x 1-on-1 mentoring sessions', included: true },
      { text: 'Career coaching & job prep', included: true },
      { text: 'Resume & LinkedIn review', included: true },
    ]
  }
];

const PricingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleEnroll = async (packageId) => {
    setLoading(packageId);
    trackButtonClick('enroll_click', { package: packageId });

    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, { withCredentials: true });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="page pricing-page">
      <Header onEnrollClick={() => {}} />
      
      <main className="page-content">
        {/* Hero */}
        <section className="page-hero pricing-hero">
          <div className="container">
            <span className="section-tag">Simple Pricing</span>
            <h1 className="page-title">Invest in Your Future</h1>
            <p className="page-subtitle">
              Choose the plan that fits your learning goals
            </p>
            <div className="guarantee-badge">
              <Shield size={16} />
              <span>30-Day Money-Back Guarantee</span>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pricing-cards-section">
          <div className="container">
            <div className="pricing-grid">
              {plans.map((plan) => (
                <div 
                  key={plan.id} 
                  className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                >
                  {plan.popular && (
                    <div className="popular-badge">
                      <Star size={14} />
                      <span>Most Popular</span>
                    </div>
                  )}

                  <div className="pricing-header">
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-description">{plan.description}</p>
                    <div className="plan-price">
                      <span className="price-current">${plan.price}</span>
                      <span className="price-original">${plan.originalPrice}</span>
                    </div>
                    <span className="price-savings">
                      Save ${plan.originalPrice - plan.price} (50% OFF)
                    </span>
                  </div>

                  <ul className="features-list">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className={feature.included ? 'included' : 'excluded'}>
                        {feature.included ? (
                          <Check className="feature-icon check" />
                        ) : (
                          <X className="feature-icon x" />
                        )}
                        <span>{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`btn-primary pricing-cta ${plan.popular ? 'popular-btn' : ''}`}
                    onClick={() => handleEnroll(plan.id)}
                    disabled={loading === plan.id}
                  >
                    {loading === plan.id ? 'Processing...' : 'Enroll Now'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="whats-included">
          <div className="container">
            <h2 className="section-title">What's Included</h2>
            <div className="included-grid">
              <div className="included-card">
                <Video className="included-icon" />
                <h4>120+ Hours of Content</h4>
                <p>Comprehensive video lessons covering every aspect of AI & ML</p>
              </div>
              <div className="included-card">
                <FileText className="included-icon" />
                <h4>25+ Projects</h4>
                <p>Real-world projects with datasets to build your portfolio</p>
              </div>
              <div className="included-card">
                <Users className="included-icon" />
                <h4>Community Access</h4>
                <p>Connect with 12,500+ students and instructors</p>
              </div>
              <div className="included-card">
                <MessageCircle className="included-icon" />
                <h4>Q&A Support</h4>
                <p>Get your questions answered within 24 hours</p>
              </div>
              <div className="included-card">
                <Clock className="included-icon" />
                <h4>Lifetime Access</h4>
                <p>Learn at your own pace with unlimited access</p>
              </div>
              <div className="included-card">
                <Award className="included-icon" />
                <h4>Certificate</h4>
                <p>Verified certificate to showcase your achievement</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Teaser */}
        <section className="pricing-faq">
          <div className="container">
            <div className="faq-teaser">
              <h3>Have Questions?</h3>
              <p>Check our FAQ or chat with our AI assistant</p>
              <Button 
                className="btn-secondary"
                onClick={() => navigate('/#faq')}
              >
                View FAQ
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
